import {
  AUTH_SESSION_STORAGE_KEY,
  config,
  HOTEL_PORTAL_SESSION_STORAGE_KEY,
} from '@/constants/config'
import { ApiError, apiErrorFromResponse, networkError, normalizeError } from '@/gateway/errors'
import type { ApiErrorBody, ListParams } from '@/types/api/common'
import type { AuthSession, AuthUser, SessionTokens, StoredSession } from '@/types/domain/auth'
import { devWarn } from '@/utils/logger'

/**
 * The single API client. Every network call in this app goes through here —
 * never `fetch`/`axios` in a component, hook body, or store.
 *
 * Responsibilities: base URL, auth header, timeout, 401 handling,
 * error normalisation, query serialisation.
 */

const DEFAULT_TIMEOUT_MS = 30_000

/**
 * Treat an access token as dead slightly early, so a request is not sent with a
 * token that expires while it is in flight.
 */
const ACCESS_TOKEN_SKEW_MS = 15_000

/**
 * Renew the pair once the access token has this much of its life left, rather
 * than waiting for it to die.
 *
 * This matters because the API currently issues BOTH tokens with the same
 * lifetime (300s each — see `AuthSessionResponse`), so there is no window in
 * which an expired access token can be exchanged using a still-valid refresh
 * token: they die together. Refreshing early means an active user's pair is
 * rotated while both halves are still good, which keeps the session alive.
 *
 * Expressed as a FRACTION of the token's own lifetime, not a fixed number of
 * seconds: a fixed lead time longer than the token's life would make every
 * single request trigger a refresh, so hardcoding one would silently double the
 * request count if the backend ever shortened `expiresIn`. As a fraction it
 * scales with whatever the server issues.
 *
 * Remove this once the backend issues a refresh token that genuinely outlives
 * the access token; the plain expiry check is enough then.
 */
const PROACTIVE_REFRESH_FRACTION = 0.2

/** Never rotate more eagerly than this, however long the token lives. */
const MAX_PROACTIVE_REFRESH_MS = 120_000

/** Set by AuthProvider so a 401 can bounce the user to /login exactly once. */
type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler | null = null
let handlingUnauthorized = false

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler
}

/**
 * Exchanges the stored refresh token for a new pair, resolving `true` when the
 * session was renewed. Registered by `gateway/modules/auth.ts` at module load —
 * the client cannot call it directly without importing the module that imports
 * the client. Resolving it here (rather than in a React effect) means the very
 * first request of the session can already refresh.
 */
type TokenRefresher = () => Promise<boolean>
let tokenRefresher: TokenRefresher | null = null

export function setTokenRefresher(refresher: TokenRefresher | null): void {
  tokenRefresher = refresher
}

/* ---------------------------------------------------------------- session -- */
/**
 * The persisted session is the ONLY thing this app stores — never app state.
 * It holds the token pair plus a snapshot of the signed-in user (name, email,
 * roles, granted permission keys).
 *
 * ## Why the user snapshot is persisted (decision, 2026-09-09)
 *
 * The API has no `GET /auth/me`, so without the snapshot the only way to learn
 * who the user is after a reload was to call `auth/refresh` — an endpoint rate
 * limited to ten calls a minute, even though the access token itself was
 * usually still valid for hours. The snapshot removes that call: a reload
 * restores the user locally and the token pair is renewed lazily, by the
 * request path, only when it is actually near expiry.
 *
 * This is a knowing exception to "no PII in storage": the same store already
 * holds the refresh token, which is strictly more sensitive than anything in
 * the snapshot. The snapshot is a UX cache, not an access control — the backend
 * checks the real permissions on every request, and the snapshot is replaced
 * wholesale every time the pair is rotated (login and every refresh), which
 * bounds how stale it can get.
 *
 * **Tokens live in `localStorage`, always** (product decision, 2026-09-09). The
 * session therefore survives closing the browser, and is shared by every tab on
 * the origin. The previous `sessionStorage` default and the Remember Me switch
 * between the two are gone: the checkbox had already been removed from the login
 * UI, so every user was silently getting the tab-scoped store and losing their
 * session whenever they closed the browser.
 *
 * ## Security note — TC-UI-L09 is OPEN, and this widens it
 *
 * Neither storage is XSS-safe: any script on the page can read both. What
 * differs is *lifetime*. A refresh token in `localStorage` stays valid for its
 * full 7 days on disk after the browser closes, so one brief scripting flaw
 * hands an attacker a week of admin access from anywhere. `sessionStorage`
 * limited the blast radius to the life of the tab.
 *
 * TC-UI-L09 moved the pair to `sessionStorage` for exactly that reason. Making
 * `localStorage` unconditional puts that exposure back for **every** user, not
 * just the ones who would have ticked a box. This is a knowing trade for
 * staying-signed-in convenience — do NOT report TC-UI-L09 as resolved.
 *
 * The correct fix remains an httpOnly, Secure, SameSite refresh cookie that page
 * scripts cannot read at all, with the "stay signed in" duration expressed as
 * the cookie's `Max-Age` so no token lifetime is decided by the client. That
 * needs a backend change — `src/screens/auth/agent.md` §23.9.
 */
function readRaw(store: Storage): string | null {
  try {
    return store.getItem(AUTH_SESSION_STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode, blocked site data).
    return null
  }
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

/**
 * Validates the persisted user snapshot. A malformed snapshot (hand-edited
 * storage, a future shape change) degrades to `null` — the session then
 * restores through the refresh call, exactly like one written before the
 * snapshot existed.
 */
function parseStoredUser(value: unknown): AuthUser | null {
  if (typeof value !== 'object' || value === null) return null

  const user = value as Partial<AuthUser>
  if (typeof user.id !== 'number' || typeof user.email !== 'string') return null
  if (typeof user.userLogin !== 'string' || typeof user.fullName !== 'string') return null
  if (!isStringArray(user.roles) || !isStringArray(user.permissions)) return null

  return {
    id: user.id,
    email: user.email,
    userLogin: user.userLogin,
    fullName: user.fullName,
    roles: user.roles,
    permissions: user.permissions,
    isSuperadmin: user.isSuperadmin === true,
  }
}

/**
 * Validates a session-shaped value from outside this module: a parsed storage
 * entry, or the message another tab broadcast after rotating the pair. Anything
 * the app did not write (or wrote in an older shape) is not a session.
 */
export function parseStoredSession(value: unknown): StoredSession | null {
  if (typeof value !== 'object' || value === null) return null

  const { accessToken, refreshToken, accessExpiresAt, refreshExpiresAt, accessLifetimeMs, user } =
    value as Partial<StoredSession>

  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null
  if (typeof accessExpiresAt !== 'number' || typeof refreshExpiresAt !== 'number') return null

  return {
    accessToken,
    refreshToken,
    accessExpiresAt,
    refreshExpiresAt,
    // A session written before this field existed still works: 0 disables
    // early renewal for it, and the next refresh writes the real lifetime.
    accessLifetimeMs: typeof accessLifetimeMs === 'number' ? accessLifetimeMs : 0,
    user: parseStoredUser(user),
  }
}

function parseSession(raw: string): StoredSession | null {
  try {
    return parseStoredSession(JSON.parse(raw))
  } catch {
    return null
  }
}

/**
 * Reads the session from `localStorage`.
 *
 * `sessionStorage` is still consulted as a fallback, and only for users who were
 * signed in under the previous build: their tokens are sitting in the old store,
 * and without this they would be logged out by the deploy. The next write moves
 * them to `localStorage` and clears the old copy, so this path retires itself.
 */
export function getStoredSession(): StoredSession | null {
  for (const store of [localStorage, sessionStorage]) {
    const raw = readRaw(store)
    if (raw === null) continue

    const session = parseSession(raw)
    if (session === null) continue

    return session
  }

  return null
}

/**
 * Writes to `localStorage`. Clearing wipes `sessionStorage` as well, so signing
 * out can never leave a usable refresh token behind in the store the previous
 * build used.
 */
export function setStoredSession(session: AuthSession | null): void {
  if (session === null) {
    for (const store of [localStorage, sessionStorage]) {
      try {
        store.removeItem(AUTH_SESSION_STORAGE_KEY)
      } catch {
        // Nothing to clear if the storage cannot be reached.
      }
    }
    return
  }

  const stored: StoredSession = { ...session.tokens, user: session.user }
  try {
    localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(stored))
  } catch {
    devWarn('localStorage unavailable — the session will not survive a reload.')
    return
  }

  // Only one copy may exist, or a session left in `sessionStorage` by the
  // previous build could outlive this one and be picked up by a later read.
  try {
    sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  } catch {
    // Unreachable storage cannot be holding a stale copy we wrote.
  }
}

/* ---------------------------------------------------- hotel portal session -- */

/**
 * The hotel-facing Secure Hotel Portal's session: one bearer token issued when
 * the hotel verifies the code from its emailed link. A separate credential
 * from the staff session — never sent to a staff endpoint, never refreshed,
 * and kept in `sessionStorage` so it dies with the tab (the portal reveals
 * payment cards). Nothing but the token and its expiry is stored.
 */
export interface HotelPortalSession {
  readonly accessToken: string
  /** Epoch ms. */
  readonly expiresAt: number
}

type HotelPortalUnauthorizedHandler = () => void
let onHotelPortalUnauthorized: HotelPortalUnauthorizedHandler | null = null

/** Set by the portal's route gate so a rejected session sends the hotel back to its link. */
export function setHotelPortalUnauthorizedHandler(
  handler: HotelPortalUnauthorizedHandler | null,
): void {
  onHotelPortalUnauthorized = handler
}

function getHotelPortalSession(): HotelPortalSession | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(HOTEL_PORTAL_SESSION_STORAGE_KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) return null
    const { accessToken, expiresAt } = value as Partial<HotelPortalSession>
    if (typeof accessToken !== 'string' || typeof expiresAt !== 'number') return null
    return expiresAt > Date.now() ? { accessToken, expiresAt } : null
  } catch {
    return null
  }
}

export function setHotelPortalSession(session: HotelPortalSession | null): void {
  try {
    if (session === null) sessionStorage.removeItem(HOTEL_PORTAL_SESSION_STORAGE_KEY)
    else sessionStorage.setItem(HOTEL_PORTAL_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    devWarn('sessionStorage unavailable — the portal session will not survive a reload.')
  }
}

/** True while a portal session exists and has not expired. */
export function hasHotelPortalSession(): boolean {
  return getHotelPortalSession() !== null
}

/** True while a refresh could still succeed, i.e. the session is restorable. */
export function hasUsableRefreshToken(): boolean {
  const session = getStoredSession()
  return session !== null && session.refreshExpiresAt > Date.now()
}

/**
 * The persisted user snapshot, but only while the session is still restorable.
 * This is how AuthProvider restores the signed-in user after a reload without
 * calling the rate-limited refresh endpoint; the token pair renews lazily in
 * the request path when it is actually near expiry.
 *
 * `null` means the snapshot cannot vouch for a session: nothing is stored, the
 * refresh token has expired, or the session predates the snapshot — the caller
 * falls back to the refresh call (or to /login).
 */
export function getStoredUser(): AuthUser | null {
  const session = getStoredSession()
  if (session === null || session.refreshExpiresAt <= Date.now()) return null
  return session.user
}

function isAccessTokenExpired(session: SessionTokens): boolean {
  return session.accessExpiresAt <= Date.now() + ACCESS_TOKEN_SKEW_MS
}

/**
 * True when the pair should be rotated now, while both halves are still valid.
 * Never fires for a session whose refresh token has already gone — that one is
 * beyond saving and is handled as an expiry.
 */
function shouldRefreshEarly(session: SessionTokens): boolean {
  const now = Date.now()
  if (session.refreshExpiresAt <= now) return false

  // Derived from the lifetime the API reported for this very token, so the lead
  // time can never exceed the token's own life and turn every request into a
  // refresh. `accessLifetimeMs` is absent on sessions stored by an older build.
  const lifetime = session.accessLifetimeMs
  if (!Number.isFinite(lifetime) || lifetime <= 0) return false

  const lead = Math.min(lifetime * PROACTIVE_REFRESH_FRACTION, MAX_PROACTIVE_REFRESH_MS)
  return session.accessExpiresAt <= now + lead
}

/**
 * Drops the session and lets AuthProvider redirect — at most once per burst.
 *
 * The de-duplication guard covers clearing the session as well as the redirect,
 * not just the redirect. A screen mounts several queries at once, so 401s arrive
 * in bursts: if every one of them wiped storage, a burst where the *first* 401
 * refreshed successfully would still be destroyed by the stragglers, whose
 * in-flight requests carried the pre-rotation token.
 */
function handleUnauthorized(): void {
  if (handlingUnauthorized) return

  handlingUnauthorized = true
  setStoredSession(null)
  onUnauthorized?.()
  window.setTimeout(() => {
    handlingUnauthorized = false
  }, 0)
}

/* ------------------------------------------------------------------ query -- */
export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[]
export type QueryParams = Readonly<Record<string, QueryValue>>

/** Serialises params, dropping empties so the URL stays a stable cache key. */
export function buildQueryString(params: QueryParams | undefined): string {
  if (!params) return ''

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item))
      continue
    }
    search.append(key, String(value))
  }

  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/** Flattens `ListParams` (including nested `filters`) into query params. */
export function listParamsToQuery(params: ListParams = {}): QueryParams {
  const { filters, ...rest } = params
  return { ...rest, ...(filters ?? {}) } as QueryParams
}

/* ----------------------------------------------------------------- request -- */
export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly query?: QueryParams
  readonly body?: unknown
  /** Multipart upload — sent as-is, no JSON content type. */
  readonly formData?: FormData
  readonly signal?: AbortSignal
  readonly timeoutMs?: number
  /** Skip the Authorization header (login, public lookups). */
  readonly skipAuth?: boolean
  /**
   * Authenticate with the hotel portal's session instead of the staff one. The
   * staff token is never attached, and a 401 ends the portal session only.
   */
  readonly portalAuth?: boolean
  /**
   * Send to the API's non-staff tree (`config.publicApiBaseUrl`, `/api/v1`)
   * instead of the staff one (`/api/v1/admin`). The backend keeps `/admin` for
   * staff authentication, so the hotel portal lives outside it. Changes only
   * the base URL — combine with `skipAuth` or `portalAuth` for the credential.
   */
  readonly publicApi?: boolean
  /**
   * Return the whole response body instead of unwrapping `{ data }`. Listing
   * endpoints need this: their pagination `meta` sits beside `data`, so
   * unwrapping would discard it.
   */
  readonly keepEnvelope?: boolean
  readonly headers?: Readonly<Record<string, string>>
}

async function parseJson<T>(response: Response): Promise<T | null> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) return null
  try {
    return (await response.json()) as T
  } catch {
    return null
  }
}

/**
 * `isRetry` marks the single re-attempt made after a successful token refresh,
 * so a request can never loop between 401 and refresh.
 */
async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const {
    method = 'GET',
    query,
    body,
    formData,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    skipAuth: skipAuthOption = false,
    portalAuth = false,
    publicApi = false,
    keepEnvelope = false,
    headers = {},
  } = options

  const baseUrl = publicApi ? config.publicApiBaseUrl : config.apiBaseUrl
  const url = `${baseUrl}${path}${buildQueryString(query)}`
  // A portal request is, as far as the staff session goes, unauthenticated: it
  // must never carry the staff token nor trigger the staff refresh or redirect.
  const skipAuth = skipAuthOption || portalAuth

  const requestHeaders: Record<string, string> = { Accept: 'application/json', ...headers }
  if (!formData && body !== undefined) requestHeaders['Content-Type'] = 'application/json'

  if (!skipAuth) {
    let session = getStoredSession()

    // The access token is short-lived. Renew it before it dies — or, if it is
    // already gone, before spending a round trip on a guaranteed 401.
    const needsRefresh = session && (isAccessTokenExpired(session) || shouldRefreshEarly(session))

    if (session && needsRefresh && !isRetry && tokenRefresher) {
      if (session.refreshExpiresAt <= Date.now()) {
        // Both tokens are past their expiry — there is nothing left to renew
        // and no request worth sending.
        handleUnauthorized()
        session = null
      } else if (await tokenRefresher()) {
        session = getStoredSession()
      } else {
        // A failed renewal is not proof the session is over: the refresher also
        // resolves `false` when the network was unreachable, and it keeps the
        // stored token in that case. Sign out only once the token is genuinely
        // gone, and otherwise let the request go out and be judged by the API.
        session = getStoredSession()
        if (!session) handleUnauthorized()
      }
    }

    if (session) requestHeaders.Authorization = `Bearer ${session.accessToken}`
  }

  if (portalAuth) {
    const portalSession = getHotelPortalSession()
    if (portalSession) requestHeaders.Authorization = `Bearer ${portalSession.accessToken}`
  }

  // Started after the refresh above, so a renewal never eats the request budget.
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true })

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      signal: controller.signal,
      ...(formData ? { body: formData } : body !== undefined ? { body: JSON.stringify(body) } : {}),
    }).catch((failure: unknown) => {
      // `fetch` rejects with a TypeError only when no response arrived at all
      // (offline, DNS, CORS). An abort stays a DOMException → timeout below.
      throw failure instanceof TypeError ? networkError() : failure
    })

    if (!response.ok) {
      const errorBody = await parseJson<ApiErrorBody>(response)
      const error = apiErrorFromResponse(response.status, errorBody)

      if (error.isUnauthorized && portalAuth) {
        setHotelPortalSession(null)
        onHotelPortalUnauthorized?.()
      }

      if (error.isUnauthorized && !skipAuth) {
        // The access token was rejected. Renew it once and replay the request;
        // the user never sees the expiry. `skipAuth` covers the refresh call
        // itself, so this cannot recurse.
        if (!isRetry && tokenRefresher && hasUsableRefreshToken() && (await tokenRefresher())) {
          window.clearTimeout(timeoutId)
          return request<T>(path, options, true)
        }

        // Sign out only when no usable token remains. A concurrent request may
        // have just rotated the pair — this 401 was answered with the old access
        // token and says nothing about the new one — and a refresh that failed
        // on the network deliberately keeps the token it could not verify.
        if (!hasUsableRefreshToken()) handleUnauthorized()
      }

      throw error
    }

    if (response.status === 204) return undefined as T

    const json = await parseJson<{ data?: T } & T>(response)
    if (json === null) return undefined as T

    // Listings need `meta`, which sits beside `data` in the envelope.
    if (keepEnvelope) return json as T

    // Unwrap `{ data: ... }` envelopes; pass bare payloads straight through.
    return (json && typeof json === 'object' && 'data' in json ? json.data : json) as T
  } catch (error) {
    throw normalizeError(error)
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'formData'>) =>
    request<T>(path, { ...options, method: options?.method ?? 'POST', formData }),
}

export { ApiError }
